import { useState, useEffect } from 'react';
import {
  Wallet,
  Calendar,
  CheckCircle,
  Clock,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Banknote,
} from 'lucide-react';
import { getOwnPaymentHistory } from '@/services/payrollService';
import type {
  StaffPaymentEntry,
  StaffPaymentHistoryResponse,
} from '@/types/payroll.types';

const MONTH_NAMES_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function formatCurrency(amount: number): string {
  return amount.toLocaleString('fr-FR') + ' MAD';
}

function formatMonthYear(month: number, year: number): string {
  return `${MONTH_NAMES_FR[month - 1]} ${year}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getStatusBadge(status: string): { label: string; className: string } {
  switch (status) {
    case 'PAYSLIP_PAID':
      return {
        label: 'Payé',
        className: 'bg-green-100 text-green-700',
      };
    case 'PAYSLIP_GENERATED':
    case 'PAYSLIP_SENT':
      return {
        label: 'En attente',
        className: 'bg-orange-100 text-orange-700',
      };
    case 'PAYSLIP_DRAFT':
      return {
        label: 'Brouillon',
        className: 'bg-gray-100 text-gray-600',
      };
    default:
      return {
        label: status,
        className: 'bg-gray-100 text-gray-600',
      };
  }
}

function getCurrentMonthStatus(entries: StaffPaymentEntry[]): {
  isPaid: boolean;
  found: boolean;
} {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const currentEntry = entries.find(
    (e) => e.month === currentMonth && e.year === currentYear,
  );

  if (!currentEntry) {
    return { isPaid: false, found: false };
  }

  return { isPaid: currentEntry.status === 'PAYSLIP_PAID', found: true };
}

export default function StaffPaymentHistoryPage() {
  const [data, setData] = useState<StaffPaymentHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 12;

  useEffect(() => {
    loadPaymentHistory();
  }, [page]);

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOwnPaymentHistory(page, limit);
      setData(response);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erreur lors du chargement';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-800 mb-1">
            Erreur de chargement
          </h3>
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <button
            onClick={loadPaymentHistory}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const entries = data?.data ?? [];
  const summary = data?.summary;
  const meta = data?.meta;
  const currentMonthStatus = getCurrentMonthStatus(entries);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Ma Paie</h1>
        <p className="text-sm text-gray-500 mt-1">
          Historique de vos paiements
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Paid This Year */}
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-blue-700">
              Total Payé cette année
            </span>
          </div>
          <p className="text-2xl font-bold text-blue-900">
            {formatCurrency(summary?.totalPaidThisYear ?? 0)}
          </p>
        </div>

        {/* Last Payment */}
        <div className="bg-green-50 rounded-xl border border-green-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
              <Banknote className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm font-medium text-green-700">
              Dernier Paiement
            </span>
          </div>
          {summary?.lastPaymentDate && summary.lastPaymentAmount != null ? (
            <div>
              <p className="text-2xl font-bold text-green-900">
                {formatCurrency(summary.lastPaymentAmount)}
              </p>
              <p className="text-xs text-green-600 mt-1">
                {formatDate(summary.lastPaymentDate)}
              </p>
            </div>
          ) : (
            <p className="text-lg font-semibold text-green-800">-</p>
          )}
        </div>

        {/* Current Month Status */}
        <div className="bg-purple-50 rounded-xl border border-purple-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm font-medium text-purple-700">
              Statut du Mois en Cours
            </span>
          </div>
          {currentMonthStatus.found ? (
            currentMonthStatus.isPaid ? (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-lg font-bold text-green-700">Payé</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-lg font-bold text-orange-700">
                  En attente
                </span>
              </div>
            )
          ) : (
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              <span className="text-lg font-bold text-gray-500">
                Non disponible
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Payment History Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-indigo-600" />
            Historique des paiements
          </h3>
        </div>

        {entries.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Mois
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Salaire Brut
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Déductions
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Ajustements
                    </th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Salaire Net
                    </th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date Paiement
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => {
                    const badge = getStatusBadge(entry.status);
                    return (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <span className="font-medium text-gray-900">
                            {formatMonthYear(entry.month, entry.year)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-sm text-gray-900">
                            {formatCurrency(entry.grossSalary)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-sm text-red-600">
                            -{formatCurrency(entry.totalDeductions)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span
                            className={`text-sm ${
                              entry.adjustmentTotal > 0
                                ? 'text-green-600'
                                : entry.adjustmentTotal < 0
                                ? 'text-red-600'
                                : 'text-gray-500'
                            }`}
                          >
                            {entry.adjustmentTotal > 0
                              ? `+${formatCurrency(entry.adjustmentTotal)}`
                              : entry.adjustmentTotal < 0
                              ? formatCurrency(entry.adjustmentTotal)
                              : '-'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCurrency(entry.netSalary)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-gray-600">
                            {entry.paidAt ? formatDate(entry.paidAt) : '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.totalPages > 1 && (
              <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Page {meta.page} sur {meta.totalPages} ({meta.total}{' '}
                  résultat{meta.total > 1 ? 's' : ''})
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                  </button>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(meta.totalPages, p + 1))
                    }
                    disabled={page >= meta.totalPages}
                    className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wallet className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun historique de paiement
            </h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Votre historique de paiements apparaîtra ici une fois les
              bulletins de paie générés.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
